import React, { useEffect, useState } from 'react';
import { useUser } from '../../../UserContext';

import KitchenSettings from './KitchenSettings';
import KitchenMenus from './KitchenMenus';
import KitchenServices from './KitchenServices';
import KitchenPaymentPreferences from './KitchenPaymentPreferences';

import { Typography, Button, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import Alert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { saveKitchenData } from '../../../services/kitchenService';
import KitchenStatusPanel from './KitchenStatusPanel';
import { useAuth } from '../../../AuthContext';
import { useKitchen } from '../../../contexts/KitchenContext';
import hubWebSocketService from '../../../services/hubWebSocketService';
import { getHubMessageFromJson } from '../../../utils/hubCommonFunctions';
import { useSnackbar } from '../../shared/SnackbarContext';
import { useOrderEvents } from '../../shared/OrderEventsContext';
import { useLocale } from '../../../i18n/LanguageContext';

const KitchenDashboard = () => {
  const { user } = useUser();
  const { auth } = useAuth();
  const { currentKitchenData, setCurrentKitchenData } = useKitchen();
  const { showMessage } = useSnackbar();
  const { triggerRefreshOrders } = useOrderEvents();
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorWithAddress, setErrorWithAddress] = useState(null);
  const [settingsExpanded, setSettingsExpanded] = useState(true);

  const isKitchenGeneralInfoComplete = () => {
    if (!currentKitchenData || errorWithAddress) return false;
    
    // Check if essential kitchen data is present
    return currentKitchenData.id !== 0 && 
           currentKitchenData.name && 
           currentKitchenData.phoneNumber &&
           currentKitchenData.address && 
           currentKitchenData.zipCode &&
           currentKitchenData.chef?.id;
  };

  const handleKitchenDataChange = (kitchenDataEdited) => {
    if (isBrandNewKitchen(kitchenDataEdited)) {
      kitchenDataEdited.chef.id = user.chef.id;
    }

    saveKitchenData(kitchenDataEdited, auth).then((updatedKitchenFromDB) => {
      if (updatedKitchenFromDB) {
        setCurrentKitchenData(updatedKitchenFromDB.kitchen);
        setErrorWithAddress(updatedKitchenFromDB.addressHasError);
        
        // Collapse settings after successful save if data is complete
        if (!updatedKitchenFromDB.addressHasError && updatedKitchenFromDB.kitchen.id !== 0) {
          setSettingsExpanded(false);
        }
      }
    }).catch((error) => {
      setError(error);
    });
    
  };

  function isBrandNewKitchen(kitchenData) {
    return kitchenData && kitchenData.id === 0;
  }

  const handleMenuDataChange = (kitchenDataEdited) => {
    setCurrentKitchenData(kitchenDataEdited);
  };

  const handleOnCancel = () => {
    if (isKitchenGeneralInfoComplete()) {
      setSettingsExpanded(false);
    }
  };

  useEffect(() => {
    // If kitchen is saved, collapse settings
    const kitchenExists = currentKitchenData?.id > 0;
    setSettingsExpanded(!kitchenExists);

    if (kitchenExists) {
      hubWebSocketService.connect({
            clientId: `chef-${currentKitchenData.id}`,
            token: auth,
            onMessage: (msg) => { 
              const message = getHubMessageFromJson(msg);
              if (message) {
                showMessage(message, 'info');
                triggerRefreshOrders();
              }
            }
      });
    }

    setLoading(false);
  }, [currentKitchenData]);

  useEffect(() => {
    const scrollToHashSection = () => {
      const hash = window.location.hash;

      if (!hash) {
        return;
      }

      const targetSection = document.getElementById(hash.replace('#', ''));

      if (targetSection && typeof targetSection.scrollIntoView === 'function') {
        targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    scrollToHashSection();
    const timeoutId = setTimeout(scrollToHashSection, 120);
    window.addEventListener('hashchange', scrollToHashSection);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('hashchange', scrollToHashSection);
    };
  }, [loading, currentKitchenData]);

  const dashboardStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '0px',
    gap: 8
  };

  const panelStyle = {
    padding: '20px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
  };

  return (
    !loading && currentKitchenData ? (
      <div style={dashboardStyle}>
        
        { error ? (<Alert severity="error">{t('kitchenDashboard.error')}</Alert>) : ''}
        
        <>
          {/* Kitchen Settings - Always first, collapsible */}
          <Accordion 
            id="kitchen-settings-section"
            expanded={settingsExpanded} 
            onChange={(e, isExpanded) => setSettingsExpanded(isExpanded)}
            sx={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="kitchen-settings-content"
              id="kitchen-settings-header"
            >
              <Typography variant="h5">{t('kitchenDashboard.title')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              { errorWithAddress ? (<Alert severity="error">{t('kitchenDashboard.addressError')}</Alert>) : ''}
              <KitchenSettings kitchenData={currentKitchenData} onDataChange={handleKitchenDataChange} onCancel={handleOnCancel}/>
            </AccordionDetails>
          </Accordion>

          {/* Show rest of panels only when kitchen is complete */}
          {isKitchenGeneralInfoComplete() && (
            <>
              <div style={panelStyle}>
                <KitchenStatusPanel kitchenData={currentKitchenData} onDataChange={handleKitchenDataChange} />
              </div>
              <div id="kitchen-menus-section" style={panelStyle}>
                <KitchenMenus kitchenData={currentKitchenData} onDataChange={handleMenuDataChange}/>
              </div>
              <div id="kitchen-services-section" style={panelStyle}>
                <KitchenServices kitchenData={currentKitchenData} onDataChange={handleKitchenDataChange}/>
              </div>
              <div id="kitchen-payment-preferences-section" style={panelStyle}>
                <KitchenPaymentPreferences kitchenData={currentKitchenData} onDataChange={handleKitchenDataChange} />
              </div>
            </>
          )}
        </>
      </div>
    ) : (
      <div>{t('kitchenDashboard.loading')}</div>
    )
  );
};

export default KitchenDashboard;
